import { useState, useMemo, useCallback, useEffect, type ReactNode } from 'react';
import type { Season } from '../api/seasonsService';
import { SeasonContext, type SeasonContextType } from './seasonContextTypes';

const STORAGE_KEY = 'fia-portal-selected-season';

export const SeasonProvider = ({ children }: { children: ReactNode }) => {
  const [selectedSeason, setSelectedSeasonState] = useState<Season | null>(() => {
    // Try to restore from localStorage on mount
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored) as Season;
      }
    } catch (error) {
      console.error('Error restoring season from localStorage:', error);
    }
    return null;
  });

  // Save to localStorage whenever season changes
  useEffect(() => {
    if (selectedSeason) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedSeason));
      } catch (error) {
        console.error('Error saving season to localStorage:', error);
      }
    }
  }, [selectedSeason]);

  // Memoize the setter function to prevent it from changing on every render
  const setSelectedSeason = useCallback((season: Season | null) => {
    setSelectedSeasonState(season);
  }, []);

  const value: SeasonContextType = useMemo(
    () => ({
      selectedSeason,
      setSelectedSeason,
      selectedYear: selectedSeason?.year || null,
    }),
    [selectedSeason, setSelectedSeason]
  );

  return <SeasonContext.Provider value={value}>{children}</SeasonContext.Provider>;
};
